import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { push } from 'connected-react-router';
import { plantSelectionEnum, plantSelectionTypesEnum } from './types';
import delay, { delayTime } from '../../utils/delay';
import axiosInstance from '../../utils/axiosInstance';
import { componentsEnum } from '../../utils/types';
import normalize from '../../utils/normalize';
import {
  makeSelectDenormEntity,
  makeSelectEntityWithAdvice,
  makeSelectEntityWithIsPickedDuringAdviceFilter,
  makeSelectEntityWithIsPresentAndPicked,
  makeSelectWithPresentAccordingToUserFilter,
} from '../../utils/reselectFactories';
import { selectAdvice } from '../floweringArch';
import { setErrorMessage } from '../genericError';
import { restartSession } from '../session';

export const getPlants = createAsyncThunk(
  'plants/fetchWithCoords',
  async (_, { getState, dispatch, rejectWithValue }) => {
    dispatch(push('/loading'));
    const startTime = performance.now();

    const { area, plants } = getState();
    const { coords } = area;

    let allPlants;
    let presentAccordingToModelIds;

    const promises = [
      axiosInstance.get('plants'),
      axiosInstance.get('plants/get-in-area', {
        params: {
          area: JSON.stringify(coords),
        },
      }),
    ];

    try {
      const [allPlantsResult, presentPlantResults] = await Promise.all(promises);
      allPlants = allPlantsResult.data.data.plants;
      presentAccordingToModelIds = presentPlantResults.data.data.ids;
    } catch (err) {
      if (!err.response) {
        dispatch(setErrorMessage('Server onbereikbaar'));
      }

      dispatch(setErrorMessage(err.response.data.message));
      dispatch(push(componentsEnum.properties[componentsEnum.plantSelectionMethod].route));
      return rejectWithValue();
    }

    if (presentAccordingToModelIds.length === 0) {
      dispatch(push(componentsEnum.properties[componentsEnum.selectArea].route));
      return rejectWithValue('Er zijn geen planten gemodelleerd voor het geselecteerde gebied.');
    }

    const endTime = performance.now();
    const elapsedMs = endTime - startTime;
    await delay(delayTime - elapsedMs);

    if (plants.selectionMethod === plantSelectionEnum.automatic) {
      dispatch(push(componentsEnum.properties[componentsEnum.plantSelection].route));
    } else {
      dispatch(push(componentsEnum.properties[componentsEnum.floweringArch].route));
    }

    const {
      ids,
      values: plantsById,
    } = normalize(allPlants);

    return {
      plantsById,
      ids,
      presentAccordingToModelIds,
    };
  },
);

const initialState = {
  plantsById: null,
  ids: [],
  presentAccordingToModelIds: [],
  presentAccordingToUserIds: [],
  pickedFromAdviceIds: [],
  selectionMethod: null,
};

export const plants = createSlice({
  name: 'plants',
  initialState,
  reducers: {
    setSelectionMethod: (state, action) => {
      state.selectionMethod = action.payload;

      switch (action.payload) {
        case plantSelectionEnum.none:
          state.selectedPlantIds = [];
          break;
        default:
          break;
      }
    },
    setSelectedPlantIds: (state, action) => {
      const {
        selectionType,
        plantIds,
      } = action.payload;
      // todo: must be one of enum
      state[selectionType] = plantIds;

      if (selectionType === plantSelectionTypesEnum.presentAccordingToUserIds) {
        state.pickedFromAdviceIds = plantIds;
      }
    },
    addSelectedPlantId: (state, action) => {
      const {
        selectionType,
        plantId,
      } = action.payload;

      // todo: must be one of enum
      state[selectionType].push(plantId);

      // If the user adds a plant in "plant-selection" we must also add this to the
      // pickedFromAdviceIds. The user can pick plants from the advice screen and after
      // that go back to the "plant-selection" and select the same plants. To prevent
      // duplicate Ids we use [...new Set]
      if (selectionType === plantSelectionTypesEnum.presentAccordingToUserIds) {
        state.pickedFromAdviceIds = state.pickedFromAdviceIds.concat(plantId);
      }
    },
    addSelectedPlantIds: (state, action) => {
      const {
        selectionType,
        plantIds,
      } = action.payload;
      // todo: must be one of enum

      state[selectionType] = state[selectionType].concat(plantIds);

      // Same as in "addSelectedPlantId"
      if (selectionType === plantSelectionTypesEnum.presentAccordingToUserIds) {
        state.pickedFromAdviceIds = state.pickedFromAdviceIds.concat(plantIds);
      }
    },
    removeSelectedPlantId: (state, action) => {
      const {
        selectionType,
        plantId,
      } = action.payload;
      // todo: must be one of enum
      state[selectionType] = state[selectionType].filter((id) => id !== plantId);

      // Same as in "addSelectedPlantId"
      if (selectionType === plantSelectionTypesEnum.presentAccordingToUserIds) {
        state.pickedFromAdviceIds = state.pickedFromAdviceIds.filter((id) => id !== plantId);
      }
    },
  },
  extraReducers: {
    [restartSession.fulfilled]: () => initialState,
    [getPlants.fulfilled]: (state, action) => {
      const {
        plantsById,
        ids,
        presentAccordingToModelIds,
      } = action.payload;

      state.plantsById = plantsById;
      state.ids = ids;
      state.presentAccordingToModelIds = presentAccordingToModelIds;
    },
  },
});

export const {
  setSelectedPlantIds,
  addSelectedPlantIds,
  addSelectedPlantId,
  removeSelectedPlantId,
  setSelectionMethod,
} = plants.actions;

/* eslint-disable max-len */
// General selectors
export const selectPlantsById = (state) => state.plants.plantsById;
export const selectIds = (state) => state.plants.ids;
export const selectDenormPlants = makeSelectDenormEntity(selectPlantsById, selectIds);
export const selectPickedFromAdviceIds = (state) => state.plants.pickedFromAdviceIds;

export const selectSelectionMethod = (state) => state.plants.selectionMethod;

// Present plants selectors
export const selectPresentAccordingToUserIds = (state) => state.plants.presentAccordingToUserIds;
export const selectPresentAccordingToModelIds = (state) => state.plants.presentAccordingToModelIds;
export const selectPlantsWithPresentInfo = makeSelectEntityWithIsPresentAndPicked(selectDenormPlants, selectPresentAccordingToModelIds, selectPresentAccordingToUserIds, selectPickedFromAdviceIds);
export const selectPresentAccordingToUserPlants = makeSelectWithPresentAccordingToUserFilter(selectPlantsWithPresentInfo, true);
export const selectNotPresentAccordingToUserPlants = makeSelectWithPresentAccordingToUserFilter(selectPlantsWithPresentInfo, false);

// Advice plants selectors
export const selectSelectedPlantsDuringAdvice = makeSelectEntityWithIsPickedDuringAdviceFilter(selectPlantsWithPresentInfo, true);
export const selectUnselectedPlantsDuringAdvice = makeSelectEntityWithIsPickedDuringAdviceFilter(selectPlantsWithPresentInfo, false);
export const selectUnselectedPlantsDuringAdviceWithImpact = makeSelectEntityWithAdvice(selectUnselectedPlantsDuringAdvice, selectAdvice);
/* eslint-enable max-len */

export default plants.reducer;
